import {
    API_TWILIO,
    API_WSP,
    MESSENGER_CHANNEL,
    WHATSAPP_CHANNEL,
} from 'src/constants/conversations';
import { TChannel, TConversationType } from 'src/interfaces/conversation';
import {
    TStoreAreaConversationChannel,
    TStoreAreas,
} from 'src/interfaces/store';
import { META_CHANNEL_PROVIDER } from './constants';
import { isChannelsModeArea } from './isChannelsModeArea';
import { TAreaSender } from './types';

const NO_SENDER: TAreaSender = Object.freeze({
    provider: null,
    senderPhone: null,
    active: false,
});

const toProvider = (
    provider: TStoreAreaConversationChannel['provider']
): TConversationType =>
    provider === META_CHANNEL_PROVIDER ? API_WSP : API_TWILIO;

interface TAreaChannelLike {
    channel?: string;
    isActive?: boolean;
    isDefault?: boolean;
}

/**
 * Active channels of an area in `channels` mode for a given channel type.
 * Structural so other conversation mappers (e.g. OmniChannel) can reuse it.
 */
export const getActiveAreaChannels = <T extends TAreaChannelLike>(
    channels: T[] = [],
    channel: string = WHATSAPP_CHANNEL
): T[] =>
    channels.filter(
        (item) => item.channel === channel && item.isActive !== false
    );

/**
 * Active WhatsApp channel of an area in `channels` mode: the default one if several are active.
 */
export const getActiveAreaChannel = (
    area: TStoreAreas
): TStoreAreaConversationChannel | null => {
    const activeChannels = getActiveAreaChannels(area.conversationChannels);
    return (
        activeChannels.find((channel) => channel.isDefault) ||
        activeChannels[0] ||
        null
    );
};

const resolveChannelsModeSender = (area: TStoreAreas): TAreaSender => {
    const channel = getActiveAreaChannel(area);
    if (!channel) return NO_SENDER;
    return {
        provider: toProvider(channel.provider),
        senderPhone: channel.sender?.phone || null,
        active: true,
    };
};

const resolveLegacySender = (
    area: TStoreAreas,
    fallbackProvider?: TConversationType
): TAreaSender => {
    const provider = area.api || fallbackProvider || null;
    if (provider === API_WSP)
        return {
            provider,
            senderPhone: area.whatsapp || null,
            active: Boolean(area.isWspAvailable),
        };
    if (provider === API_TWILIO)
        return {
            provider,
            senderPhone: area.phone || null,
            active: Boolean(area.isWspAvailable),
        };
    return NO_SENDER;
};

/**
 * Single source of the sender (provider + number) of an area for a channel.
 * - Messenger: Twilio from `area.fb`.
 * - WhatsApp in `channels` mode: the active channel of `conversationChannels`.
 * - WhatsApp in `legacy` mode: `area.api` (or the conversation provider) → `phone` / `whatsapp`.
 */
export const resolveAreaSender = (
    area: TStoreAreas | null,
    channel: TChannel,
    conversationProvider?: TConversationType
): TAreaSender => {
    if (!area) return NO_SENDER;
    if (channel === MESSENGER_CHANNEL)
        return {
            provider: API_TWILIO,
            senderPhone: area.fb || null,
            active: Boolean(area.fb),
        };
    if (isChannelsModeArea(area)) return resolveChannelsModeSender(area);
    return resolveLegacySender(area, conversationProvider);
};
